// Miro two-way asset sharing (REF-6702) — hand-typed against the agreed contract
// (`REF-6702-plan.md` §D). The v3 endpoints are NOT in the generated OpenAPI snapshot yet, so these
// types are written by hand; once the snapshot carries `/v1/integrations/miro/*`, prefer the
// generated shapes. Never hand-edit `src/api/generated/**`.
//
// Error contract: Miro-side problems come back as CODED errors (409/422/404/429/502) and never as
// 401/403 (a 401 would log the user out). Read the code with `getMiroErrorCode` (utils/miro).

export interface IMiroUser {
  id: string;
  name: string | null;
}

export interface IMiroTeam {
  id: string;
  name: string | null;
}

export interface IMiroStatusResponse {
  connected: boolean;
  /** Refresh failed / revoked — the user must connect again. */
  needs_reauth?: boolean;
  /** `false` when the BE has no Miro app configured; the status then carries nothing else. */
  available?: boolean;
  miro_user?: IMiroUser | null;
  miro_team?: IMiroTeam | null;
  connected_at?: string | null;
}

export interface IMiroAuthorizeRequest {
  /** Relative app path (`/…`, never `//`) to come back to after consent. */
  return_to: string;
}

export interface IMiroAuthorizeResponse {
  authorize_url: string;
}

export interface IMiroCallbackRequest {
  code: string;
  state: string;
}

export interface IMiroCallbackResponse {
  connected: true;
  return_to: string;
  miro_user: IMiroUser | null;
  miro_team: IMiroTeam | null;
}

export interface IMiroDisconnectResponse {
  connected: false;
}

export interface IMiroBoardsRequest {
  query?: string;
  offset?: number;
  /** ≤ 50 */
  limit?: number;
}

export interface IMiroBoard {
  id: string;
  name: string;
  description?: string | null;
  /** Nullable from the BE — never open a board without one. */
  view_link: string | null;
  role: "viewer" | "commenter" | "editor" | "coowner" | "owner" | string;
  /** role ∈ {editor, coowner, owner} — the only boards an export may target. */
  can_write: boolean;
  modified_at?: string | null;
}

export interface IMiroBoardsResponse {
  items: IMiroBoard[];
  total: number;
  offset: number;
  limit: number;
}

export type MiroExportSource = "design" | "asset" | "trend";

export interface IMiroExportItem {
  source: MiroExportSource;
  source_id: string;
  /** FULL-RESOLUTION url — never `thumbnail_url` / a `previewUrl()` render (`[DISPLAY_URL_NEVER_OUTBOUND]`). */
  image_url: string;
  title?: string;
}

export interface IMiroExportRequest {
  /** 1–100 */
  items: IMiroExportItem[];
}

export interface IMiroExportResult {
  /** Index into the request's `items`. */
  index: number;
  status: "created" | "failed";
  miro_item_id?: string;
  error_code?: string;
}

export interface IMiroExportResponse {
  board: { id: string; view_link: string | null };
  results: IMiroExportResult[];
}

export interface IMiroBoardImagesRequest {
  cursor?: string | null;
  /** ≤ 50 */
  limit?: number;
}

export interface IMiroBoardImage {
  id: string;
  title?: string | null;
  /** Expires in ~60s — refetch the page when the <img> fails to load. */
  preview_url: string;
  preview_expires_at?: string | null;
  width?: number | null;
  height?: number | null;
}

export interface IMiroBoardImagesResponse {
  items: IMiroBoardImage[];
  cursor: string | null;
}

export interface IMiroImportRequest {
  /** 1–100 */
  item_ids: string[];
}

export interface IMiroImportedAsset {
  id: string | number;
  url: string;
  thumbnail_url?: string | null;
}

export interface IMiroImportResult {
  item_id: string;
  status: "imported" | "duplicate" | "failed";
  asset?: IMiroImportedAsset;
  error_code?: string;
}

export interface IMiroImportResponse {
  results: IMiroImportResult[];
}

// Send / import run as BE jobs (`…/export-jobs`, `…/import-jobs`), 1–100 items each.
export const MIRO_MAX_EXPORT_ITEMS = 100;
export const MIRO_MAX_IMPORT_ITEMS = 100;
// Moodboard job caps (`…/moodboard-jobs`): the BE answers 422 above these, so the FE trims and says so.
export const MIRO_MAX_MOODBOARD_IMAGES = 60;
export const MIRO_MAX_MOODBOARD_PRINTS = 20;
export const MIRO_MAX_MOODBOARD_FABRICS = 20;
export const MIRO_MAX_MOODBOARD_COLORS = 30;

/** 202 body of every `…-jobs` start route. */
export interface IMiroJobAccepted {
  job_id: string;
  status: "queued";
  total: number;
}

export type MiroJobStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

// `title` and `label` (section captions) are layout furniture, not something the user picked: results lists and counts skip them.
export type MiroMoodboardPart = "title" | "label" | "image" | "print" | "fabric" | "color";

/** A moodboard job's per-element result — the export shape plus the part it belongs to. */
export interface IMiroMoodboardResult extends IMiroExportResult {
  kind?: MiroMoodboardPart;
}

/** A canvas export's per-element result. `skipped` is not a failure: the element has nothing Miro can show (e.g. an empty frame). */
export interface IMiroCanvasResult {
  element_id: string;
  status: "created" | "failed" | "skipped";
  miro_item_id?: string;
  error_code?: string;
  /**
   * What the result is (BE contract): `main` (a main image; also what an omitted kind means), `version` (an edit version placed under
   * its image) or `label` (a caption text the BE adds for layout). Only the first two are the user's own items.
   * `version_id` names the version a `version` result is about.
   */
  kind?: "main" | "version" | "label";
  version_id?: string;
}

/** An `import_canvas` per-image result (BE !398): no per-image `skipped` — non-image items only appear in `result.skipped`. */
export interface IMiroCanvasImportResult {
  item_id: string;
  status: "created" | "duplicate" | "failed";
  error_code?: string;
}

/** The comment counts of a canvas job result: export → created / updated / unchanged / failed; import → imported / updated / duplicate. */
export interface IMiroJobComments {
  created?: number;
  updated?: number;
  unchanged?: number;
  failed?: number;
  imported?: number;
  duplicate?: number;
  /** Export: stickies of threads deleted from the canvas, pruned on re-export. */
  removed?: number;
  /** Import: stickies beyond the 500-per-job cap. */
  skipped?: number;
  /** A comments problem never fails the images: the job still succeeds and says why here (`miro.rate_limited`, `miro.comments_unavailable`, …). */
  error_code?: string;
}

/**
 * The canvas STICKY NOTE counts of a canvas job result (REF-7145): import (a realtime canvas: each Miro sticky became a
 * note) → imported / updated / duplicate / failed / skipped; export (the canvas's notes became Miro stickies) → created /
 * updated / unchanged / failed. A notes problem never fails the images: the job succeeds and says why in `error_code`.
 */
export interface IMiroJobNotes {
  imported?: number;
  updated?: number;
  duplicate?: number;
  failed?: number;
  skipped?: number;
  created?: number;
  unchanged?: number;
  error_code?: string;
}

export interface IMiroJobResult {
  /** `export_canvas` / `import_canvas` (a classic canvas): what happened to the comment threads / sticky notes. */
  comments?: IMiroJobComments | null;
  /** `export_canvas` / `import_canvas` (a realtime canvas): what happened to the canvas's sticky notes. */
  notes?: IMiroJobNotes | null;
  board?: { id: string; view_link: string | null };
  /** Export / moodboard: `IMiroExportResult[]` (moodboard: + `kind`); import: `IMiroImportResult[]`; canvas: `IMiroCanvasResult[]`; import_canvas: `IMiroCanvasImportResult[]`. */
  results: Array<IMiroExportResult | IMiroMoodboardResult | IMiroImportResult | IMiroCanvasResult | IMiroCanvasImportResult>;
  /** A refusal that hit MID-job (token died, board went read-only): the job still `succeeded`, unfinished items `failed` with it. */
  error_code?: string;
  /** `import_canvas` only: the Design Canvas the board became (`null` + `error_code: miro.nothing_to_import` when nothing could be imported), how many images it holds (duplicates included) and the items left out, by type. */
  canvas?: { id: string; name: string } | null;
  imported?: number;
  /** `import_canvas` (BE !414): explicit counts, `imported = created + duplicate`. Absent on older BEs → derived from `results`. */
  created?: number;
  duplicate?: number;
  skipped?: Record<string, number>;
  /** `import_canvas`: the BE did not count every skipped item, so `skipped` is a lower bound ("at least N"). */
  skipped_partial?: boolean;
  /** `moodboard_from_board`: the folder upload the deck became (`null` + `error_code` when no moodboard was made); its id is the moodboard status poll's. */
  upload?: IMiroBoardMoodboardUpload | null;
  /** `moodboard_from_board`: images the job meant to use / that reached the deck / that Miro would not give. */
  images?: number;
  included?: number;
}

/** `GET jobs/{job_id}`. `result` appears once `succeeded`; `failed` means the job itself broke (`error` is text). */
export interface IMiroJobProgress {
  done: number;
  total: number;
}

export interface IMiroJob {
  job_id: string;
  status: MiroJobStatus;
  /** While queued / running: items done so far (moves in steps). `null` / absent = unknown → show an indeterminate bar. */
  progress?: IMiroJobProgress | null;
  result?: IMiroJobResult | null;
  error?: string | null;
}

export interface IMiroMoodboardImage {
  url: string;
  title?: string;
}

export interface IMiroMoodboardColor {
  hex: string;
  name?: string;
  code?: string;
}

export const MIRO_MAX_REPORT_ID = 64;

export interface IMiroMoodboardJobRequest {
  title: string;
  /** The moodboard report this block comes from (1–64 chars) — the BE only logs it. */
  report_id?: string;
  images?: IMiroMoodboardImage[];
  prints?: IMiroMoodboardImage[];
  fabrics?: IMiroMoodboardImage[];
  colors?: IMiroMoodboardColor[];
}

/** `GET boards/{id}/images/{item_id}/original` — the full-resolution image; the url expires. */
export interface IMiroOriginalImage {
  url: string;
  expires_at?: string | null;
}

/** One row of `GET jobs?active=true` — what a reload needs to resume watching (or announce) a job. */
export interface IMiroJobSummary {
  job_id: string;
  kind: "export" | "import" | "moodboard" | "export_moodboard" | "export_canvas" | "import_canvas" | "moodboard_from_board";
  board?: { id: string; view_link: string | null; name?: string | null } | null;
  status: MiroJobStatus;
  progress?: IMiroJobProgress | null;
  created_at?: string | null;
  finished_at?: string | null;
}

// ── Design Canvas export (layout-preserving) ────────────────────────────────────────────────────────────────────────

/** `POST boards` — a board the user does not have yet. */
export interface IMiroCreateBoardRequest {
  name: string;
}

export interface IMiroCreatedBoard {
  id: string;
  name: string;
  view_link: string | null;
}

/** `POST canvas-projects/{canvas_id}/export-jobs`: exactly one of `board_id` / `new_board_name`; no `element_ids` = the whole canvas. */
export interface IMiroCanvasExportRequest {
  board_id?: string;
  new_board_name?: string;
  element_ids?: string[];
  /** Place each image's other edit versions under it, with their prompt. The BE treats an omitted field as `true`. */
  include_versions?: boolean;
  /** One Miro sticky note per comment thread. Default false (BE); the FE always sends it explicitly. */
  include_comments?: boolean;
}

export interface IMiroCanvasExportAccepted extends IMiroJobAccepted {
  board?: { id: string; view_link: string | null } | null;
}

/** Miro board names are 1–60 characters (`POST boards`, `new_board_name`). */
export const MIRO_MAX_BOARD_NAME = 60;

// ── Miro board → Design Canvas ──────────────────────────────────────────────────────────────────────────────────────

/**
 * `POST boards/{board_id}/canvas-import-jobs` — images and sticky notes; `name` defaults (BE side) to the board's name.
 * Sticky notes ALWAYS come in (REF-7145): native notes on a realtime canvas, comment threads on a classic one. The BE
 * still accepts `include_comments` but ignores it for stickies, so the dialog no longer sends it.
 */
export interface IMiroCanvasImportRequest {
  include_comments?: boolean;
  name?: string;
}

/** The 202: `total` is unknown up front (the BE counts the board's images while it works). */
export interface IMiroCanvasImportAccepted {
  job_id: string;
  status: "queued";
  total: number | null;
}

// ── Miro board (or one frame) → Refabric moodboard ─────────────────────────────────────────────────────────────────

/** The BE refuses a whole board / frame above this many images (422 `miro.too_many_items`) — before anything is made. */
export const MIRO_MAX_BOARD_MOODBOARD_IMAGES = 200;
export const MIRO_MAX_BOARD_MOODBOARD_NAME = 120;

/** One row of `GET boards/{id}/frames`, in reading order. `title` is null for an untitled frame. */
export interface IMiroFrame {
  id: string;
  title: string | null;
  image_count: number;
  /** Colour swatches found inside the frame. Absent on a backend that does not detect colours yet. */
  colors?: IMiroBoardColor[];
}

/** A colour swatch read off the board (`#RRGGBB`), named when the board names it. */
export interface IMiroBoardColor {
  hex: string;
  name?: string | null;
}

/** The backend takes at most this many colours on the start call. */
export const MIRO_MAX_BOARD_MOODBOARD_COLORS = 100;

export interface IMiroFramesResponse {
  frames: IMiroFrame[];
  /** What the WHOLE board would give: the frames' images plus the ones on the board itself. */
  board_image_count: number;
  /** Colour swatches of the WHOLE board. Absent on a backend that does not detect colours yet. */
  board_colors?: IMiroBoardColor[];
}

/** `POST boards/{id}/moodboard-from-board-jobs`. No `frame_id` = the whole board; `report_name` is read in `single` mode only. */
export interface IMiroMoodboardFromBoardRequest {
  frame_id?: string;
  mode?: "single" | "multi";
  report_name?: string;
  /** Single mode only. Omitted = the backend uses the colours it detected; `[]` = none; at most 100. */
  colors?: { hex: string; name?: string }[];
}

/** `result.upload` of a `moodboard_from_board` job: `job_id` is the moodboard folder upload (`GET trends/moodboard-folder/{job_id}`). */
export interface IMiroBoardMoodboardUpload {
  job_id: string;
  mode: "single" | "multi";
  report_name?: string | null;
  files?: number;
  pages?: number;
}

/** 202 of the start route — `total` is null (pages are counted as the deck is built). */
export interface IMiroMoodboardFromBoardAccepted {
  job_id: string;
  status: "queued";
  total: number | null;
}
